import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react'
import interact from 'interactjs'
import { TimelineMax as Timeline } from 'gsap'

import ScrollDownImage from '../images/scroll-down.svg'
import CloseIcon from '../icons/close.svg'
import ToggleIcon from '../icons/up.svg'

export const STATUS = {
  CLOSE: 1,
  OPEN: 2,
  MINIMIXED: 3
}

const InfoPanel = (props) => {
  const { maxHeight, minHeigth, children, role, className, onClose, defaultStatus=STATUS.OPEN } = props
  const [inner, setInner] = useState(children)
  const [status, setStatus] = useState(STATUS.CLOSE)
  const [scrolled, setScrolled] = useState(false)
  const [currentSize, setCurrentSize] = useState(minHeigth)
  const ref = useRef()
  const timeline = useRef()
  const container = useRef()
  const content = useRef()
  const updateSizeTimeout = useRef()
  const hasScroll = (minHeigth + 5) < currentSize
  const hasScrollBar = (maxHeight + 5) < currentSize
  const minimizedTimlineProgress = (minHeigth / maxHeight)
  const openTimlineProgress = (Math.min(maxHeight, currentSize) / maxHeight)

  useEffect(() => {
    ref.current = {
      status: status,
      maxHeight: maxHeight,
      minimizedTimlineProgress: minimizedTimlineProgress,
      openTimlineProgress: openTimlineProgress
    }
  }, [status, minimizedTimlineProgress, maxHeight, openTimlineProgress])

  const updateSize = useCallback((delay) => {
    clearTimeout(updateSizeTimeout.current)
    updateSizeTimeout.current = setTimeout(() => {
      if (!content.current) return
      setCurrentSize(parseInt(content.current.scrollHeight))
    }, delay || 300)
  }, [])

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      updateSize()
    })
    resizeObserver.observe(content.current)
    return () => resizeObserver.disconnect()
  }, [updateSize])

  useEffect(() => {
    const contentInstance = content.current
    const scrollHandler = () => {
      const currentScroll = contentInstance.scrollTop
      const scrolled = currentScroll > 55
      setScrolled(scrolled)
    }
    contentInstance.addEventListener('scroll', scrollHandler)
    return () => contentInstance.removeEventListener('scroll', scrollHandler)
  }, [])

  useEffect(() => {
    timeline.current = new Timeline({ paused: true })
    timeline.current.fromTo(
      container.current,
      { y: '100%' },
      { y: '0%', duration: 0.3, ease: 'none' }
    )
    return () => timeline.current.clear()
  }, [])

  useEffect(() => {
    let dragMove, currentProgress

    const dragStartHandler = (event) => {
      dragMove = 0
      currentProgress = timeline.current.progress()
      currentScroll = content.current.scrollTop
    }


    const dragMoveHandler = (event) => {
      dragMove += event.dy
      const progress = currentProgress - (dragMove / ref.current.maxHeight)
      if (progress > 1){
        content.current.scrollTop = currentScroll - dragMove
        return
      }
      timeline.current.pause()
      timeline.current.progress(progress)
    }

    const dragEndHandler = (event) => {
      dragMove = null
      const progress = timeline.current.progress()
      const { status, minimizedTimlineProgress, openTimlineProgress } = ref.current
      let nextstatus = status

      if (status === STATUS.OPEN && progress < (minimizedTimlineProgress - 0.2)) nextstatus = STATUS.CLOSE
      else if (status === STATUS.OPEN && progress < 0.8) nextstatus = STATUS.MINIMIXED
      else if (status === STATUS.MINIMIXED && progress > (minimizedTimlineProgress + 0.2)) nextstatus = STATUS.OPEN
      else if (status === STATUS.MINIMIXED && progress < (minimizedTimlineProgress - 0.2)) nextstatus = STATUS.CLOSE

      if (status !== nextstatus) {
        setStatus(nextstatus)
      } else {
        if (status === STATUS.MINIMIXED) {
          timeline.current.tweenTo(minimizedTimlineProgress * 0.3)
        }
        if (status === STATUS.OPEN) {
          timeline.current.tweenTo(openTimlineProgress * 0.3)
        }
      }
    }

    const interactInstance = interact(container.current).draggable({
      onstart: dragStartHandler,
      onmove: dragMoveHandler,
      onend: dragEndHandler,
      startAxis: 'y',
      lockAxis: 'y'
    }).styleCursor(false)

    return () => interactInstance.unset()
  }, [])

  useEffect(() => {
    if (children) {
      setInner(children)
    } else {
      setStatus(STATUS.CLOSE)
    }
  }, [children])

  useEffect(() => {
    if (inner) {
      content.current.scrollTop = 0
      setScrolled(false)
      if (ref.current.status === STATUS.CLOSE) {
        updateSize(0)
        setStatus(defaultStatus)
      } else {
        updateSize()
      }
    }   
  }, [inner, updateSize, defaultStatus])

  useEffect(() => {
    if (status === STATUS.MINIMIXED) {
      timeline.current.tweenTo(minimizedTimlineProgress * 0.3)
    }
    if (status === STATUS.OPEN) {
      timeline.current.tweenTo(openTimlineProgress * 0.3)
    }
    if (status === STATUS.CLOSE && timeline.current.progress() !== 0) {
      timeline.current.tweenTo(0).then(() => {
        content.current.scrollTop = 0
        setInner(null)
        if (onClose) onClose()
      })
    }
  }, [status, minimizedTimlineProgress, openTimlineProgress, onClose])

  const toggleHandler = useCallback(() => {
    setStatus(ref.current.status === STATUS.OPEN ? STATUS.MINIMIXED : STATUS.OPEN)
  }, [])

  const closeHandler = useCallback(() => {
    setStatus(STATUS.CLOSE)
  }, [])

  let containerClassName = `pwa__infopanel ${className || ''}`
  let contentClassName = 'pwa__infopanel__content'
  let toggleClassName = 'pwa__infopanel__toggle button raised fade-in delay'

  if (status !== STATUS.MINIMIXED) {
    if (hasScrollBar) contentClassName += ' scrollbar'
    if (scrolled) contentClassName += ' scrolled'
  }

  if (status === STATUS.MINIMIXED) {
    toggleClassName += ' minimixed'
  }

  return (
    <div
      ref={container}
      className={containerClassName}
      aria-hidden={(inner) ? 'false' : 'true'}
      role={role || 'modal'}
      style={{ height: maxHeight ? `${maxHeight}px` : 'auto', display: inner ? 'block' : 'none' }}>

      { hasScroll && (
          <button className={toggleClassName} onClick={toggleHandler}>
            <ToggleIcon />
          </button>
      )}

      <button className="pwa__infopanel__close button raised circular fade-in delay" onClick={closeHandler}>
        <CloseIcon />
      </button>
      <div ref={content} className={contentClassName}>
        { false && (
          <div className="pwa__infopanel__scrollmore">
            <ScrollDownImage />
          </div>
        )}
        {inner}
      </div>

    </div>
  )
}

export default InfoPanel
